import * as React from 'react';
import type { FieldValues, UseFormRegister, Path } from 'react-hook-form';

export const onlyDigits = (s: string) => s.replace(/[^\d]/g, '');
export const withThousands = (s: string) =>
  s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/**
 * Use like:
 *   const commaInput = useCommaNumberInput(register);
 *   <input {...commaInput('assetMileage', rules)} />
 *
 * It preserves RHF’s register wiring AND formats while typing.
 */
export function useCommaNumberInput<TFieldValues extends FieldValues>(
  register: UseFormRegister<TFieldValues>
) {
  return function commaInput<Name extends Path<TFieldValues>>(
    name: Name,
    rules?: Parameters<typeof register<Name>>[1]
  ) {
    const reg = register(name as any, rules);

    return {
      ...reg,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        // sanitize and format
        const digits = onlyDigits(e.target.value);
        const formatted = digits ? withThousands(digits) : '';

        // update the visible value
        e.target.value = formatted;

        // pass the (formatted) event to RHF
        reg.onChange(e);
      },
    };
  };
}

export const parseCommaNumber = (s: string | number | undefined | null) => {
  if (s == null) return 0;
  if (typeof s === 'number') return s;
  const digits = onlyDigits(s);
  return digits ? Number(digits) : 0;
};
